import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import classNames from 'classnames';
import Downshift from 'downshift';
import * as React from 'react';
import styled from 'styled-components';
import { KEYS } from '../../constants/keys.js';
import { useTranslations } from '../../hooks/use_translations.js';
import { spacing2x } from '../../styles/spacing.js';
import { useThemeContext } from '../../styles/themes/themes.js';
import { bindRefs } from '../../utils/react.js';
import { Box } from '../Box/Box.js';
import { IconSearch } from '../Icons/IconSearch.js';
import { InputContainer } from '../Input/InputContainer.js';
import { InputInput } from '../Input/InputInput.js';
import { ListItem } from '../ListItem/ListItem.js';
import { Popper } from '../Popper/Popper.js';
import { FetchDropdownItems } from './FetchDropdownItems.js';
import { Spinner } from '../Spinner/Spinner.js';
import { Tag } from '../Tag/Tag.js';
import { jsx, jsxs } from 'react/jsx-runtime';

var _excluded = ["aria-label", "aria-labelledby", "containerSelector", "fetchResults", "filterOptions", "inline", "items", "limit", "onChange", "placeholder", "popperProps", "startAdornment", "style", "value", "zIndex"],
  _excluded2 = ["className", "disabled", "emailStyle", "endAdornment", "error", "lightStyle", "readOnly", "success"],
  _excluded3 = ["triggerRef"];
var DropdownItemContainer = styled(Box).withConfig({
  displayName: "Search__DropdownItemContainer",
  componentId: "sc-18fs9zi-0"
})(["&&{margin-block:4px;", ";}"], function (_ref) {
  var noBorder = _ref.noBorder;
  return noBorder && " border-width: 0;";
});
var MenuWrapper = styled.div.withConfig({
  displayName: "Search__MenuWrapper",
  componentId: "sc-18fs9zi-1"
})(["&&{width:320px;max-height:300px;overflow-y:auto;}"]);
var StyledTag = styled(Tag).withConfig({
  displayName: "Search__StyledTag",
  componentId: "sc-18fs9zi-2"
})(["&&{margin-block:1px;margin-inline-end:3px;}"]);
var StyledInputContainer = styled(InputContainer).withConfig({
  displayName: "Search__StyledInputContainer",
  componentId: "sc-18fs9zi-3"
})(["&&{flex-grow:1;min-height:32px;max-height:120px;overflow-y:auto;height:auto;padding-block:2px;padding-inline-start:8px;padding-inline-end:0;", ";}&&:has(> [class*='__StyledTag']){padding-inline-start:3px;}"], function (_ref2) {
  var shouldWrap = _ref2.shouldWrap;
  return shouldWrap && "flex-wrap: wrap;";
});
var Search = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var _useThemeContext = useThemeContext(),
    localization = _useThemeContext.localization;
  var _useTranslations = useTranslations(localization === null || localization === void 0 ? void 0 : localization.translations),
    t = _useTranslations.t;
  var ariaLabel = props["aria-label"],
    ariaLabelledBy = props["aria-labelledby"],
    containerSelector = props.containerSelector,
    fetchResults = props.fetchResults,
    filterOptions = props.filterOptions,
    _props$inline = props.inline,
    inline = _props$inline === void 0 ? true : _props$inline,
    _props$items = props.items,
    items = _props$items === void 0 ? [] : _props$items,
    limit = props.limit,
    onChange = props.onChange,
    _props$placeholder = props.placeholder,
    placeholder = _props$placeholder === void 0 ? t("Search.defaultPlaceholder") : _props$placeholder,
    _props$popperProps = props.popperProps,
    popperProps = _props$popperProps === void 0 ? {} : _props$popperProps,
    _props$startAdornment = props.startAdornment,
    startAdornment = _props$startAdornment === void 0 ? /*#__PURE__*/jsx(IconSearch, {
      size: 16
    }) : _props$startAdornment,
    _props$style = props.style,
    inputComponentStyles = _props$style === void 0 ? {} : _props$style,
    _props$value = props.value,
    value = _props$value === void 0 ? [] : _props$value,
    zIndex = props.zIndex,
    otherInputProps = _objectWithoutProperties(props, _excluded);
  var className = otherInputProps.className,
    disabled = otherInputProps.disabled,
    emailStyle = otherInputProps.emailStyle,
    endAdornment = otherInputProps.endAdornment,
    error = otherInputProps.error,
    lightStyle = otherInputProps.lightStyle,
    readOnly = otherInputProps.readOnly,
    success = otherInputProps.success,
    inputInputProps = _objectWithoutProperties(otherInputProps, _excluded2);
  var fetchedItemsLength = React.useRef(0);
  var inputContainerRef = React.useRef(null);
  var prevSelectedValues = React.useRef(value);
  var downshiftRef = React.useRef({});
  var inputRef = React.useRef(null);
  var _React$useState = React.useState(""),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    searchText = _React$useState2[0],
    setSearchText = _React$useState2[1];
  var handleSelect = function handleSelect(selectedValue) {
    if (onChange) {
      prevSelectedValues.current = value;
      var updatedValues = [].concat(_toConsumableArray(value), [selectedValue]);
      var filteredValues = updatedValues.filter(function (v) {
        return v !== void 0;
      });
      onChange(filteredValues);
    }
    setSearchText("");
  };
  var removeSelectedValue = function removeSelectedValue(val) {
    if (onChange) {
      prevSelectedValues.current = value;
      onChange(value.filter(function (selectedVal) {
        return selectedVal.value !== val.value;
      }));
    }
  };
  var _onKeyDown = function onKeyDown(event) {
    if (readOnly) {
      return;
    }
    switch (event.key) {
      case KEYS.BACKSPACE:
        if (value.length && !searchText) {
          removeSelectedValue(value[value.length - 1]);
        }
        break;
    }
  };
  var handleInputBlur = function handleInputBlur() {
    var _inputContainerRef$cu;
    (_inputContainerRef$cu = inputContainerRef.current) === null || _inputContainerRef$cu === void 0 ? void 0 : _inputContainerRef$cu.scrollTo(0, 0);
  };
  React.useEffect(function () {
    if (value.length > prevSelectedValues.current.length) {
      var _downshiftRef$current;
      (_downshiftRef$current = downshiftRef.current) === null || _downshiftRef$current === void 0 ? void 0 : _downshiftRef$current.reset();
    }
  }, [value]);
  var itemToString = function itemToString(i) {
    return i ? i.children : "";
  };
  var filterItems = filterOptions ? filterOptions : function (item, input) {
    return !input || item.children.toLowerCase().includes(input.toLowerCase());
  };
  return /*#__PURE__*/jsx(Downshift, {
    onSelect: handleSelect,
    itemToString: itemToString,
    selectedItem: null,
    defaultHighlightedIndex: 0,
    ref: downshiftRef,
    children: function children(_ref3) {
      var clearItems = _ref3.clearItems,
        getInputProps = _ref3.getInputProps,
        getItemProps = _ref3.getItemProps,
        getMenuProps = _ref3.getMenuProps,
        highlightedIndex = _ref3.highlightedIndex,
        isOpen = _ref3.isOpen,
        setHighlightedIndex = _ref3.setHighlightedIndex;
      var DropdownItemContainerComponent = function DropdownItemContainerComponent(_ref4) {
        var dropdownItems = _ref4.dropdownItems,
          loading = _ref4.loading;
        var filteredItems = dropdownItems.filter(function (item) {
          return filterItems(item, searchText);
        });
        var filteredItemsWithoutSelectedValues = filteredItems.filter(function (item) {
          return value.findIndex(function (selectedVal) {
            return selectedVal.value === item.value;
          }) < 0;
        });
        fetchedItemsLength.current = filteredItemsWithoutSelectedValues.length;
        return /*#__PURE__*/jsxs(DropdownItemContainer, {
          elevation: 1,
          borderRadius: 1,
          noBorder: filteredItemsWithoutSelectedValues.length === 0 && !loading,
          children: [loading && /*#__PURE__*/jsx("div", {
            style: {
              margin: 8
            },
            children: /*#__PURE__*/jsx(Spinner, {})
          }), /*#__PURE__*/jsx(MenuWrapper, _objectSpread(_objectSpread({}, getMenuProps({
            "aria-labelledby": ariaLabelledBy
          })), {}, {
            children: filteredItemsWithoutSelectedValues.map(function (item, index) {
              return /*#__PURE__*/jsx(ListItem, _objectSpread(_objectSpread({}, getItemProps({
                key: item.value,
                index: index,
                item: item
              })), {}, {
                startAdornment: item.startAdornment,
                active: highlightedIndex === index,
                children: item.children
              }), index);
            })
          }))]
        });
      };
      DropdownItemContainerComponent.displayName = "DropdownItemContainerComponent";
      var dropdownContainer = null;
      if (isOpen) {
        if (fetchResults) {
          dropdownContainer = /*#__PURE__*/jsx(FetchDropdownItems, {
            fetchResults: fetchResults,
            inputText: searchText,
            onLoaded: function onLoaded() {
              clearItems();
              setHighlightedIndex(0);
            },
            render: DropdownItemContainerComponent
          });
        } else if (items.length) {
          dropdownContainer = DropdownItemContainerComponent({
            dropdownItems: items
          });
        }
      } else {
        dropdownContainer = null;
      }
      return /*#__PURE__*/jsx("div", {
        className: classNames("_mntl-search mntl-outline-focus-within", className),
        style: _objectSpread(_objectSpread({}, {
          display: "flex",
          minWidth: "100px"
        }), inputComponentStyles),
        children: /*#__PURE__*/jsx(Popper, _objectSpread(_objectSpread({
          inline: inline,
          zIndex: zIndex,
          containerSelector: containerSelector
        }, popperProps), {}, {
          showing: isOpen,
          renderTrigger: function renderTrigger(_ref5) {
            var triggerRef = _ref5.ref;
            return /*#__PURE__*/jsxs(StyledInputContainer, {
              error: error,
              disabled: disabled,
              success: success,
              emailStyle: emailStyle,
              endAdornment: endAdornment,
              lightStyle: lightStyle,
              readOnly: readOnly,
              ref: bindRefs(inputContainerRef, triggerRef),
              focusRef: inputRef,
              shouldWrap: !!value.length,
              startAdornment: value.length ? void 0 : startAdornment,
              style: {
                paddingInlineEnd: endAdornment ? spacing2x : "auto"
              },
              children: [value.map(function (val, index) {
                return /*#__PURE__*/jsx(StyledTag, {
                  value: itemToString(val),
                  startAdornment: val === null || val === void 0 ? void 0 : val.startAdornment,
                  onRemove: readOnly ? void 0 : function () {
                    return removeSelectedValue(val);
                  },
                  disabled: disabled
                }, index);
              }), /*#__PURE__*/jsx(InputInput, _objectSpread(_objectSpread(_objectSpread({
                readOnly: readOnly
              }, inputInputProps), getInputProps(_objectSpread({
                onChange: function onChange(event) {
                  return setSearchText(event.target.value);
                },
                onKeyDown: function onKeyDown(event) {
                  return _onKeyDown(event);
                },
                "aria-label": ariaLabel,
                "aria-labelledby": ariaLabelledBy
              }, fetchedItemsLength.current < 1 ? {
                "aria-activedescendant": void 0
              } : {}))), {}, {
                as: "input",
                disabled: disabled || (limit ? value.length >= limit : void 0),
                ref: bindRefs(inputRef, ref),
                placeholder: value.length ? "" : placeholder,
                autosize: true,
                onBlur: handleInputBlur,
                value: searchText
              }))]
            });
          },
          renderContent: function renderContent(_ref6) {
            _ref6.triggerRef;
              var rest = _objectWithoutProperties(_ref6, _excluded3);
            return /*#__PURE__*/jsx("div", _objectSpread(_objectSpread({}, rest), {}, {
              children: dropdownContainer
            }));
          }
        }))
      });
    }
  });
});
Search.displayName = "Search";

export { Search };
